import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles, Users, Heart, Share2, ExternalLink, ArrowLeft } from "lucide-react";
import { mbtiInfo, clubInfo } from "@/lib/mbti";

/**
 * Public personal landing page — /voice/:id
 * Shareable URL for consultants to promote themselves on social networks.
 * Shows ONLY public, non-sensitive info: name, photo, club, MBTI, team, padrinho.
 * Includes a "Junta-te à minha equipa" CTA that opens the magic-link signup.
 */
export default function VoiceProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/voice/profile/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Perfil não encontrado ou inativo."));
  }, [id]);

  const joinUrl = data ? `${window.location.origin}/register?ref=${data.invite_ref}` : "";

  const share = async () => {
    const url = `${window.location.origin}/voice/${id}`;
    const text = data ? `Conhece ${data.name} na KW Lead Growth League` : "";
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); return; }
      catch (e) {
        // User cancelled the share sheet OR share API failed silently.
        // Fall through to clipboard fallback below.
        if (e?.name !== "AbortError") console.debug("navigator.share failed:", e);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado para a área de transferência.");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const copyJoinLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast.success("Link de convite copiado.");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center p-6" data-testid="voice-profile-error">
        <h1 className="font-display text-3xl font-black uppercase">Perfil indisponível</h1>
        <p className="text-[#A3A3A3] mt-2">{error}</p>
        <Link to="/voice" className="kw-btn-secondary mt-6 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Voltar ao Mural Social
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="voice-profile-loading">
        <p className="text-[#737373]">A carregar perfil…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="voice-profile-page">
      {/* Top nav */}
      <div className="border-b border-[#222] px-6 py-3 flex items-center justify-between">
        <Link to="/voice" className="text-xs uppercase tracking-widest text-[#737373] hover:text-white flex items-center gap-1" data-testid="voice-profile-back">
          <ArrowLeft className="w-3 h-3" /> Mural Social
        </Link>
        <button onClick={share} className="text-xs uppercase tracking-widest text-[#737373] hover:text-white flex items-center gap-1" data-testid="voice-profile-share">
          <Share2 className="w-3 h-3" /> Partilhar
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Hero — photo + name */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-8" data-testid="voice-profile-hero">
          {data.photo_url ? (
            <img src={data.photo_url} alt={data.name}
              className="w-44 h-44 md:w-52 md:h-52 rounded-full object-cover border-4 border-[#E50000] flex-shrink-0" />
          ) : (
            <div className="w-44 h-44 md:w-52 md:h-52 rounded-full bg-[#1A1A1A] flex items-center justify-center text-5xl font-display font-black text-[#E50000] border-4 border-[#E50000] flex-shrink-0">
              {(data.name || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="text-center md:text-left flex-1">
            <div className="kw-overline text-[#E50000] mb-2">{data.league_name}</div>
            <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none" data-testid="voice-profile-name">
              {data.name}
            </h1>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-4">
              {data.club && (() => {
                const ci = clubInfo(data.club);
                return (
                  <span
                    className="text-[11px] uppercase tracking-widest font-bold px-3 py-1 border"
                    style={{ color: ci?.color, borderColor: ci?.border, background: ci?.bg }}
                    data-testid="voice-profile-club">
                    {data.club}
                  </span>
                );
              })()}
              {data.mbti && (() => {
                const info = mbtiInfo(data.mbti) || (data.club ? clubInfo(data.club) : null);
                return (
                  <span
                    className="text-[11px] uppercase tracking-widest font-bold px-3 py-1 border"
                    style={{ color: info?.color, borderColor: info?.border, background: info?.bg }}
                    data-testid="voice-profile-mbti">
                    {data.mbti}{data.personality_label ? ` · ${data.personality_label}` : ""}
                  </span>
                );
              })()}
              {data.team_name && (
                <span className="text-[11px] uppercase tracking-widest font-bold px-3 py-1 border border-[#EAB308] text-[#EAB308] inline-flex items-center gap-1" data-testid="voice-profile-team">
                  <Users className="w-3 h-3" /> {data.team_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Padrinho card */}
        {data.padrinho && (
          <section className="mt-12 border border-[#222] p-5" data-testid="voice-profile-padrinho">
            <div className="kw-overline text-[#737373] mb-3 flex items-center gap-2">
              <Heart className="w-3 h-3" /> Padrinho / Madrinha
            </div>
            <div className="flex items-center gap-4">
              {data.padrinho.photo_url ? (
                <img src={data.padrinho.photo_url} alt={data.padrinho.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-[#222]" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#1A1A1A] flex items-center justify-center text-xs font-bold text-[#737373]">
                  {(data.padrinho.name || "?").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div>
                <Link to={`/voice/${data.padrinho.id}`} className="font-display text-lg font-black uppercase hover:text-[#E50000] transition-colors">
                  {data.padrinho.name}
                </Link>
                <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                  {data.padrinho.club || "—"} {data.padrinho.mbti ? `· ${data.padrinho.mbti}` : ""}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CTA — join via magic link */}
        <section className="mt-12 border-2 border-[#E50000] bg-[#E50000]/5 p-6 md:p-8" data-testid="voice-profile-cta">
          <Sparkles className="w-6 h-6 text-[#E50000] mb-3" />
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight leading-tight">
            Junta-te à equipa<br />de {data.name?.split(" ")[0]}
          </h2>
          <p className="text-[#A3A3A3] mt-3 text-sm md:text-base">
            Vais entrar na KW Lead Growth League através deste convite. {data.name?.split(" ")[0]} recebe <strong className="text-white">+20 pts</strong> quando fizeres o teu primeiro reporting.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href={joinUrl} className="kw-btn-primary inline-flex items-center gap-2" data-testid="voice-profile-join">
              <ExternalLink className="w-4 h-4" /> Aceitar convite →
            </a>
            <button onClick={copyJoinLink} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="voice-profile-copy-link">
              Copiar link
            </button>
          </div>
        </section>

        {/* Footer */}
        <section className="mt-12 pt-8 border-t border-[#222] text-center">
          <p className="text-[10px] uppercase tracking-widest text-[#737373]">
            Página pública · KW Lead Growth League
          </p>
          <Link to="/voice" className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white mt-2 inline-block" data-testid="voice-profile-explore">
            Explora o Mural Social →
          </Link>
        </section>
      </div>
    </div>
  );
}
